/**
 * Athena CCDA XML Parser
 *
 * Parses CCDA (C-CDA) format XML files from Athena EHR system.
 * Extracts basic patient demographics and returns minimal patient data.
 * Full clinical data (labs, vitals, medications) should come from supplemental JSON files.
 */

export interface AthenaPatientData {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  mrn: string;
  gender: string;
}

/**
 * Format CCDA date (YYYYMMDD) to ISO format (YYYY-MM-DD)
 */
function formatCcdaDate(ccdaDate: string): string {
  if (!ccdaDate || ccdaDate.length < 8) return '';

  const year = ccdaDate.substring(0, 4);
  const month = ccdaDate.substring(4, 6);
  const day = ccdaDate.substring(6, 8);

  return `${year}-${month}-${day}`;
}

/**
 * Extract text content from first matching element
 */
function getElementText(parent: Element, tagName: string): string {
  const element = parent.querySelector(tagName);
  return element?.textContent?.trim() || '';
}

/**
 * Get attribute value from first matching element
 */
function getElementAttribute(parent: Element, tagName: string, attributeName: string): string {
  const element = parent.querySelector(tagName);
  return element?.getAttribute(attributeName) || '';
}

/**
 * Parse Athena CCDA XML and extract patient demographics
 */
export function parseAthenaCcdaXml(xmlContent: string): AthenaPatientData {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlContent, 'text/xml');

  const parserError = doc.querySelector('parsererror');
  if (parserError) {
    throw new Error(`XML parsing error: ${parserError.textContent}`);
  }

  // CCDA structure: ClinicalDocument > recordTarget > patientRole > patient
  const patientRole = doc.querySelector('ClinicalDocument recordTarget patientRole');
  if (!patientRole) {
    throw new Error('Could not find patientRole in CCDA XML');
  }

  const patient = patientRole.querySelector('patient');
  if (!patient) {
    throw new Error('Could not find patient in CCDA XML');
  }

  // Extract name (may have multiple <name> elements, use the one with use="L" for legal name)
  const nameElement = patient.querySelector('name[use="L"]') || patient.querySelector('name');
  const firstName = nameElement ? getElementText(nameElement, 'given') : 'Unknown';
  const lastName = nameElement ? getElementText(nameElement, 'family') : 'Unknown';

  // Extract birthdate
  const birthTimeValue = getElementAttribute(patient, 'birthTime', 'value');
  const dateOfBirth = formatCcdaDate(birthTimeValue);

  // Extract gender
  const genderCode = getElementAttribute(patient, 'administrativeGenderCode', 'code');
  const genderDisplay = getElementAttribute(patient, 'administrativeGenderCode', 'displayName');
  const gender = genderDisplay || (genderCode === 'M' ? 'Male' : genderCode === 'F' ? 'Female' : 'Unknown');

  // Extract MRN from patientRole id
  const idElement = patientRole.querySelector('id');
  const mrn = idElement?.getAttribute('extension') || 'Unknown';

  return {
    firstName,
    lastName,
    dateOfBirth,
    mrn,
    gender
  };
}

/**
 * Detect if XML content is in CCDA format (Athena EHR)
 */
export function isCcdaFormat(xmlContent: string): boolean {
  // Check for CCDA-specific markers
  return xmlContent.includes('ClinicalDocument') &&
         xmlContent.includes('urn:hl7-org:v3') &&
         (xmlContent.includes('athenahealth') || xmlContent.includes('2.16.840.1.113883.10.20.22'));
}
